import { hWebDb } from '@/plugins/dexie';
import { ISettings } from '@/shared/interfaces/ISettings';
import { normalizeAllApiSettings } from '@/utils/settings-validation';
import { markBootConfigFromApi, markBootConfigFromCache } from '@/utils/boot-config-source';
import { isPublicAuthPath } from '@/utils/public-auth-path';
import { useHFetch } from './http/use-http-request';
import { apiSettings, resolveActiveAppClientCode } from './use-settings';
import { publicSettings } from './use-public-settings';
import { effectScope, watch } from 'vue';

type HelixHttpRequest = ReturnType<typeof useHFetch>;

const CACHE_NAME = 'settings';
const ENTRY_NAME = 'settings';

/** Keys that must never be written to IndexedDB (UI configuration only). */
const SENSITIVE_SETTINGS_KEY_PATTERN = /token|password|secret|personal_?id|email/i;

/** Warn once per key name for the lifetime of the page. */
const warnedSensitiveSettingsKeys = new Set<string>();

function isSensitiveSettingsKey(key: string): boolean {
  return SENSITIVE_SETTINGS_KEY_PATTERN.test(key);
}

/**
 * Hub `public: true` settings are the keys returned by anonymous `GET /public/settings`.
 * `/my/config/settings` is a flat value map and does not include the catalog flag.
 */
function isPublicSettingsKey(key: string): boolean {
  return Object.hasOwn(publicSettings, key);
}

/**
 * IndexedDB may only keep Hub-public, non-sensitive settings.
 * Authenticated `/my/config/settings` is still applied in memory in full.
 * Warn only when a sensitive key is public (it would otherwise be persisted).
 */
export function filterSettingsForIndexedDb(settings: Record<string, unknown>): Record<string, unknown> {
  const safe: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(settings)) {
    if (!isPublicSettingsKey(key)) {
      continue;
    }

    if (isSensitiveSettingsKey(key)) {
      if (!warnedSensitiveSettingsKeys.has(key)) {
        warnedSensitiveSettingsKeys.add(key);
        console.warn(`[settings] Refusing to persist sensitive settings key "${key}" to IndexedDB`);
      }
      continue;
    }

    safe[key] = value;
  }

  return safe;
}

/** Captured during plugin install so refresh works outside Vue setup (e.g. setInterval). */
let cachedSettingsHttpClient: HelixHttpRequest | null = null;

let initialSettingsLoadStarted = false;

let resolveSettingsLoad: (() => void) | null = null;

/** Last non-null app_client_code seen — used to detect tenant churn (not the initial boot). */
let previousNonNullAppClientCode: string | null = null;

let clientCodeCacheWatcherStarted = false;

export const settingsLoadPromise = new Promise<void>((resolve) => {
  resolveSettingsLoad = resolve;
});

function markSettingsLoaded() {
  resolveSettingsLoad?.();
  resolveSettingsLoad = null;
}

async function cacheSettings(settings: ISettings, timestamp?: string) {
  const toStore = filterSettingsForIndexedDb(settings as Record<string, unknown>) as ISettings;

  await hWebDb.settings.clear();
  await hWebDb.settings.add({ key: ENTRY_NAME, value: toStore });

  if (timestamp) {
    await hWebDb.cacheKeys.put({ key: timestamp, name: CACHE_NAME }, CACHE_NAME);
    return;
  }

  console.warn('[settings] Hub omitted settings last_modifieddate; settings cached without timestamp (will revalidate next boot).');
}

async function clearCachedSettingsBlob() {
  try {
    await hWebDb.settings.clear();
    await hWebDb.cacheKeys.delete(CACHE_NAME);
  } catch (error) {
    console.warn('[settings] Failed to clear settings cache after app_client_code change:', error);
  }
}

function normalizeAppClientCode(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

/** Purge persisted settings when client code changes from one non-null value to another. */
export async function applyAppClientCodeCachePolicy(previousNonNull: string | null, nextRaw: unknown): Promise<string | null> {
  const code = normalizeAppClientCode(nextRaw);

  if (!code) {
    return previousNonNull;
  }

  if (previousNonNull !== null && previousNonNull !== code) {
    await clearCachedSettingsBlob();
  }

  return code;
}

function watchAppClientCodeForCachePurge() {
  if (clientCodeCacheWatcherStarted) {
    return;
  }
  clientCodeCacheWatcherStarted = true;

  const scope = effectScope(true);
  scope.run(() => {
    watch(
      () => resolveActiveAppClientCode(),
      async (rawCode) => {
        previousNonNullAppClientCode = await applyAppClientCodeCachePolicy(previousNonNullAppClientCode, rawCode);
      },
      { flush: 'sync' },
    );
  });
}

async function fetchAndApplySettings(settingsUrl: string, helixHttpRequest: HelixHttpRequest, lastModifiedDate?: string) {
  let rawResponse: Awaited<ReturnType<ReturnType<HelixHttpRequest>['response']>> | undefined;
  try {
    rawResponse = await helixHttpRequest({ url: `${settingsUrl}` }).response();
  } catch (error) {
    // Soft-fail: keep bundled / cached settings. Common before auth cookie exists (401).
    console.warn('[settings] Authenticated settings fetch failed; keeping prior settings:', error);
  }

  if (!rawResponse) {
    return;
  }

  const newSettings = await rawResponse.json<Record<string, string>>().catch(() => undefined);

  if (!newSettings || typeof newSettings !== 'object') {
    console.warn('[settings] Authenticated settings fetch returned invalid JSON; keeping prior settings.');
    return;
  }

  const normalizedSettings = normalizeAllApiSettings(newSettings);

  // Apply to in-memory state first — runtime must not depend on IndexedDB succeeding.
  Object.assign(apiSettings, normalizedSettings);
  markBootConfigFromApi();

  try {
    await cacheSettings(normalizedSettings, lastModifiedDate);
  } catch (error) {
    // Cache is best-effort; maintenance and other settings still apply above.
    console.warn('[settings] Failed to write settings cache; runtime settings still applied:', error);
  }

  return normalizedSettings;
}

async function loadSettingsFromCache(): Promise<boolean> {
  try {
    const cached = await hWebDb.settings.get(ENTRY_NAME);

    if (cached?.value) {
      Object.assign(apiSettings, normalizeAllApiSettings(cached.value as Record<string, unknown>));
      markBootConfigFromCache();
      return true;
    }
  } catch (error) {
    console.warn('[settings] Failed to read settings cache; will fetch from API:', error);
  }

  return false;
}

async function getCachedSettingsTimestamp(): Promise<string | undefined> {
  try {
    return (await hWebDb.cacheKeys.get(CACHE_NAME))?.key;
  } catch (error) {
    console.warn('[settings] Failed to read settings cache timestamp:', error);
    return undefined;
  }
}

/**
 * Checks `settingsUrl/_timestamp` and only fetches full settings when the timestamp changed.
 * Mirrors the translations sync pattern in use-update-translations.
 */
async function syncSettingsFromApi(settingsUrl: string, helixHttpRequest: HelixHttpRequest, { ensureLoaded = false } = {}) {
  const cachedTimestamp = await getCachedSettingsTimestamp();

  const res = await helixHttpRequest({ url: `${settingsUrl}/_timestamp` }, { throwHttpErrors: false }).response();
  if (!res || res.status === 204 || res.ok === false) {
    if (ensureLoaded) {
      await loadSettingsFromCache();
    }
    return;
  }
  const { last_modifieddate } = (await res?.json<{ last_modifieddate: string }>()) ?? {};

  let isCacheInvalid = true;

  if (last_modifieddate) {
    isCacheInvalid = cachedTimestamp !== last_modifieddate;
  } else if (!cachedTimestamp) {
    console.warn('[settings] Hub omitted settings last_modifieddate; fetching full payload.');
  }

  if (ensureLoaded) {
    // IndexedDB is public-only, so it is not a complete snapshot. Apply it for a
    // fast first paint, then always fetch authenticated settings into memory.
    await loadSettingsFromCache();
    await fetchAndApplySettings(settingsUrl, helixHttpRequest, last_modifieddate || undefined);
    return;
  }

  if (isCacheInvalid) {
    await fetchAndApplySettings(settingsUrl, helixHttpRequest, last_modifieddate || undefined);
  }
}

/** Loads settings on page boot — uses cache when the timestamp has not changed. */
async function loadSettingsFromApi(settingsUrl: string, helixHttpRequest: HelixHttpRequest) {
  await syncSettingsFromApi(settingsUrl, helixHttpRequest, { ensureLoaded: true });
}

function resolveSettingsHttpClient(provided?: HelixHttpRequest): HelixHttpRequest {
  if (provided) {
    return provided;
  }

  if (cachedSettingsHttpClient) {
    return cachedSettingsHttpClient;
  }

  return useHFetch();
}

/** Polls `_timestamp` and fetches settings only when they have changed. */
export async function refreshAppSettings(settingsUrl = 'my/config/settings', helixHttpRequest?: HelixHttpRequest) {
  if (isPublicAuthPath()) {
    return;
  }

  await syncSettingsFromApi(settingsUrl, resolveSettingsHttpClient(helixHttpRequest));
}

export function useHUpdateSettings(settingsUrl = 'my/config/settings', helixHttpRequest?: HelixHttpRequest) {
  if (initialSettingsLoadStarted) {
    return;
  }
  initialSettingsLoadStarted = true;

  if (isPublicAuthPath()) {
    markSettingsLoaded();
    return;
  }

  const httpClient = resolveSettingsHttpClient(helixHttpRequest);
  cachedSettingsHttpClient = httpClient;

  watchAppClientCodeForCachePurge();

  loadSettingsFromApi(settingsUrl, httpClient)
    .then(() => {
      markSettingsLoaded();
    })
    .catch((error) => {
      console.warn('[settings] Initial authenticated settings load failed; continuing on bundled defaults:', error);
      markSettingsLoaded();
    });
}

/** Test helper — resets module gates so specs can re-enter useHUpdateSettings. */
export function resetUpdateSettingsStateForTests() {
  initialSettingsLoadStarted = false;
  clientCodeCacheWatcherStarted = false;
  previousNonNullAppClientCode = null;
  warnedSensitiveSettingsKeys.clear();
  cachedSettingsHttpClient = null;
}
